import React from 'react'
import { Image } from './types/models'
import { useTranslation } from 'react-i18next'
import MyButton from '../buttons'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import { FolderOpen } from 'lucide-react'
import { IoIosArrowDown } from 'react-icons/io'

interface GalleryUsageInfoProps {
  images: Image[]
  onStorageCheck: (newSize: number) => boolean
}

const calculateTotalSize = (images: Image[]): number => {
  return images.reduce((acc, img) => acc + img.size, 0)
}

const GalleryUsageInfo: React.FC<GalleryUsageInfoProps> = ({
  images,
  onStorageCheck,
}) => {
  const { t } = useTranslation()
  const totalSize = calculateTotalSize(images)
  const maxSize = 10
  const usagePercentage = (totalSize / maxSize) * 100
  const [showUsageInfo, setShowUsageInfo] = React.useState(false)

  return (
    <div className='w-full'>
      <div className='bg-anthracite text-white justify-left relative flex w-full px-4 py-3.5 text-center rounded-lg mb-1'>
        <div className='flex items-center gap-2 whitespace-nowrap'>
          <FolderOpen className='w-5 h-5 text-redpink' />
          <p className='text-sm sm:text-base'>{t('main-gallery')}</p>
        </div>
        <MyButton
          variant='anthracite'
          className='absolute right-0 top-0.5 text-redpink  rounded-full '
          onClick={(e: { preventDefault: () => void }) => {
            e.preventDefault()
            setShowUsageInfo(!showUsageInfo)
          }}
        >
          {usagePercentage > 90 && (
            <>
              <span className='text-sm text-redpink ml-2'>
                {totalSize.toFixed(2)} MB
              </span>
              <AiOutlineInfoCircle className='text-3xl text-redpink' />
            </>
          )}

          {usagePercentage > 70 && usagePercentage <= 90 && (
            <>
              <span className='text-sm text-yellow-400 ml-2'>
                {totalSize.toFixed(2)} MB
              </span>
              <AiOutlineInfoCircle className='text-3xl text-yellow-400' />
            </>
          )}
          {usagePercentage <= 70 && (
            <>
              <span className='text-sm text-whatsapp ml-2'>
                {totalSize.toFixed(2)} MB
              </span>
              <AiOutlineInfoCircle className='text-3xl text-whatsapp' />
            </>
          )}
          <IoIosArrowDown className='text-xl text-white' />
        </MyButton>
      </div>

      {showUsageInfo && (
        <div className=' rounded-lg p-1 mb-2'>
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-2 '>
            <div>
              <h3 className='text-xl font-bold mb-2  text-redpink'>
                {t('gallery-guide')}
              </h3>
              <div className='space-y-2'>
                <div className='flex items-start space-x-2 text-sm'>
                  <div className='min-w-5 h-5 rounded-full bg-anthracite flex items-center justify-center mt-1'>
                    <span className='text-white'>1</span>
                  </div>
                  <p className='text-gray-600'>{t('guide-upload')}</p>
                </div>
                <div className='flex items-start space-x-2 text-sm'>
                  <div className='min-w-5 h-5 rounded-full bg-anthracite flex items-center justify-center mt-1'>
                    <span className='text-white'>2</span>
                  </div>
                  <p className='text-gray-600'>{t('guide-organize')}</p>
                </div>
                <div className='flex items-start space-x-2 text-sm'>
                  <div className='min-w-5 h-5 rounded-full bg-anthracite flex items-center justify-center mt-1'>
                    <span className='text-white'>3</span>
                  </div>
                  <p className='text-gray-600'>{t('guide-albums')}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className='text-xl font-bold mb-2 text-whatsapp'>
                {t('storage-usage')}
              </h3>
              <div className='space-y-4'>
                <div>
                  <div className='flex justify-between mb-1'>
                    <span className='text-sm font-medium text-gray-700'>
                      {t('storage-used', {
                        size: totalSize.toFixed(2),
                        max: maxSize,
                      })}
                    </span>
                    <span className='text-sm font-medium text-gray-700'>
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-2.5'>
                    <div
                      className={`h-2.5 rounded-full ${
                        usagePercentage > 90
                          ? 'bg-red-600'
                          : usagePercentage > 70
                          ? 'bg-yellow-400'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <div className='bg-grisclair p-4 rounded-lg'>
                    <div className='text-sm text-gray-500'>
                      {t('total-images')}
                    </div>
                    <div className='text-2xl font-bold text-gray-700'>
                      {images.length}
                    </div>
                  </div>
                  <div className='bg-grisclair p-4 rounded-lg'>
                    <div className='text-sm text-gray-500'>
                      {t('average-size')}
                    </div>
                    <div className='text-2xl font-bold text-gray-700'>
                      {(totalSize / (images.length || 1)).toFixed(2)} MB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GalleryUsageInfo
